const normalizeText = (value) => String(value || "").trim().toLowerCase();

// Verified aliases only. The final server-side path additionally uses the
// provider-scoped CS2 registry; these aliases keep checkout feedback in parity.
const VERIFIED_TEAM_ALIASES = {
  flu: "hltv:11837",
  fluxo: "hltv:11837",
  "ex mibr a": "hltv:13977",
  "ex mibr academy": "hltv:13977",
  "mibr academy": "hltv:13977",
};

const stripMapScope = (value) => normalizeText(value)
  .replace(/\bmaps?\s*1\s*[-_]\s*2\b.*$/i, "")
  .trim();

export const normalizeCs2TeamIdentity = (value) => {
  const normalized = stripMapScope(value)
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) return "";
  return VERIFIED_TEAM_ALIASES[normalized] || `name:${normalized}`;
};

const getTeam = (leg) => normalizeCs2TeamIdentity(leg?.team_short || leg?.team || leg?.team_abbr);
const getOpponent = (leg) => normalizeCs2TeamIdentity(leg?.opponent_short || leg?.opponent || leg?.opponent_abbr);
const getMatchId = (leg) => {
  const value = leg?.match_id ?? leg?.matchId ?? leg?.game_id ?? leg?.gameId ?? leg?.event_id ?? leg?.eventId;
  const normalized = String(value ?? "").trim();
  return normalized && normalized.toLowerCase() !== "unknown" ? normalized : "";
};
const getStartValue = (leg) => normalizeText(leg?.start_time_iso || leg?.start_time_obj || leg?.start_time);
const getStartMs = (value) => {
  if (!value) return NaN;
  const parsed = Date.parse(value.includes("T") ? value : value.replace(" ", "T"));
  return Number.isFinite(parsed) ? parsed : NaN;
};

export const getCs2MatchIdentity = (leg) => {
  const teams = [getTeam(leg), getOpponent(leg)].filter(Boolean).sort();
  const start = getStartValue(leg);
  return {
    matchId: getMatchId(leg),
    teams: teams.length === 2 ? teams : [],
    start,
    startMs: getStartMs(start),
  };
};

export const areSameCs2Match = (left, right, toleranceMs = 15 * 60 * 1000) => {
  const a = left?.teams ? left : getCs2MatchIdentity(left);
  const b = right?.teams ? right : getCs2MatchIdentity(right);
  if (a.matchId && b.matchId && a.matchId === b.matchId) return true;
  if (a.teams.length !== 2 || b.teams.length !== 2 || a.teams[0] !== b.teams[0] || a.teams[1] !== b.teams[1]) {
    return false;
  }
  if (a.start && b.start && a.start === b.start) return true;
  return Number.isFinite(a.startMs) && Number.isFinite(b.startMs) && Math.abs(a.startMs - b.startMs) <= toleranceMs;
};

export const assignCs2MatchKeys = (legs) => {
  const identities = legs.map((leg) => getCs2MatchIdentity(leg?.raw || leg));
  const parents = identities.map((_, index) => index);
  const find = (index) => {
    let root = index;
    while (parents[root] !== root) root = parents[root];
    while (parents[index] !== index) {
      const next = parents[index];
      parents[index] = root;
      index = next;
    }
    return root;
  };
  const join = (left, right) => {
    const leftRoot = find(left);
    const rightRoot = find(right);
    if (leftRoot !== rightRoot) parents[rightRoot] = leftRoot;
  };

  for (let left = 0; left < identities.length; left += 1) {
    for (let right = left + 1; right < identities.length; right += 1) {
      if (areSameCs2Match(identities[left], identities[right])) join(left, right);
    }
  }

  return identities.map((identity, index) => {
    const root = find(index);
    const rootIdentity = identities[root];
    if (rootIdentity.teams.length === 2 && rootIdentity.start) {
      return `canonical:${rootIdentity.teams.join("|")}|start:${rootIdentity.start}`;
    }
    return rootIdentity.matchId ? `match:${rootIdentity.matchId}` : `match-group:${root}`;
  });
};

export const getCs2CanonicalMatchKey = (leg) => {
  const identity = getCs2MatchIdentity(leg);
  if (identity.teams.length !== 2 || !identity.start) return "";
  return `cs2|canonical:${identity.teams.join("|")}|start:${identity.start}`;
};
